import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Node } from 'reactflow';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { RootState } from '../app/store';
import { updateAutomationData } from '../features/automation/automationSlice';
import DatePicker from 'react-datepicker';
// @ts-ignore
import 'react-datepicker/dist/react-datepicker.css';
import { MdClose } from 'react-icons/md';
import { FiChevronDown, FiX, FiCheck } from 'react-icons/fi';
import { RxCross1 } from 'react-icons/rx';
import { toast } from 'react-toastify';
import { getAuthContext } from '../utils/jwt';
import { BASE_URL } from '../utils/constants';
import EmailPreviewModal from './EmailPreviewModal';

const MdCloseComponent = MdClose as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiCheckComponent = FiCheck as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiCrossComponent = RxCross1 as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

type UnitType = 'days' | 'weeks' | 'months';

interface Condition {
  category: string;
  operator: string;
  value: string;
}

interface Bucket {
  name: string;
  weight: number;
  to?: string;
}

interface MultiSplitRule {
  id: string;
  condition: any;
  to: string;
  label: string;
}

interface Campaign {
  id: string;
  orgId: string;
  topicId: string | null;
  segmentSnapshotIds: string | null;
  transactional: boolean;
  title: string;
  subject: string;
  type: string | null;
  status: string;
  templateId: string;
  senderProfileId: string;
  segmentIds: string[];
  tagIds: string[];
  senderIds: string | null;
  sendMode: string;
  scheduledAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  approvedBy: string | null;
  scheduledBy: string | null;
  createdAt: string;
  updatedAt: string;
  abEnabled: boolean;
  abType: string | null;
  abWinnerMetric: string | null;
  abEvalWindowHours: number | null;
  abTestSamplePct: number | null;
  deletedAt: string | null;
  template: {
    id: string;
    orgId: string;
    name: string;
    description: string;
    status: string;
    version: number;
    folderId: string;
    assetId: string | null;
    blocks: Array<{ text: string; type: string }>;
    styles: string;
    metadata: any;
    capability: any;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    thumbnailUrl: string | null;
  };
  senderProfile: {
    id: string;
    orgId: string;
    senderName: string;
    senderEmail: string;
    verified: boolean;
    createdAt: string;
    deletedAt: string | null;
  };
  segments: Array<{
    id: string;
    orgId: string;
    name: string;
    description: string | null;
    type: string;
    rulesJson: any;
    createdAt: string;
    updatedAt: string;
  }>;
  tags: any[];
  matrics?: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    openRate: number;
    clickRate: number;
    bounced: number;
    unsubscribed: number;
  };
}

interface NodeData {
  label: string;
  id: string;
  type?: string;
  tag?: string | string[];
  selectedTags?: string[];
  template?: string;
  variantId?: string;
  variables?: { name: string };
  delayType?: 'fixed' | 'specific';
  amount?: number;
  unit?: 'days' | 'weeks' | 'months';
  date?: string;
  time?: string;
  conditions?: Condition[];
  maxWaitAmount?: number;
  maxWaitUnit?: string;
  buckets?: Bucket[];
  isMultiSplit?: boolean;
  multiSplitRules?: MultiSplitRule[];
  defaultTo?: string;
  isBranch?: boolean;
  branchConditions?: any[];
  conditionLogic?: 'AND' | 'OR';
}

interface DetailPanelProps {
  node: Node<NodeData> | null;
  onUpdateNode?: (updatedNode: Node<NodeData>) => void;
  readOnly?: boolean;
}

interface TagWithTimestamp {
  name: string;
  createdAt: number;
}

const formScanSchema = yup.object({
  url: yup
    .string()
    .url('Please enter a valid URL')
    .required('URL is required')
    .matches(/^https?:\/\//, 'URL must start with http:// or https://'),
  tag: yup.string().when('nodeType', {
    is: (val: string) => val === 'tags',
    then: (schema) => schema.required('Tag is required'),
  }),
});

const DetailPanel: React.FC<DetailPanelProps> = React.memo(
  ({ node, onUpdateNode, readOnly = false }) => {
    const dispatch = useDispatch();
    const automationData = useSelector((state: RootState) => state.automation);

    const [localTrigger, setLocalTrigger] = useState(automationData.trigger || '');
    const [condition, setCondition] = useState('');
    const [scanError, setScanError] = useState('');

    // Campaigns
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');

    // Preview Modal States
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewHtml, setPreviewHtml] = useState<string>('');
    const [previewLoading, setPreviewLoading] = useState(false);

    // Tags
    const [allTags, setAllTags] = useState<TagWithTimestamp[]>([]);
    const [loadingTags, setLoadingTags] = useState(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
    const [tagSearchTerm, setTagSearchTerm] = useState('');
    const [newTagName, setNewTagName] = useState('');
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [isCreatingTag, setIsCreatingTag] = useState(false);
    const [tagValidationError, setTagValidationError] = useState('');
    const tagsDropdownRef = useRef<HTMLDivElement>(null);

    // Percentage split
    const [buckets, setBuckets] = useState<Bucket[]>([
      { name: 'A', weight: 50 },
      { name: 'B', weight: 50 },
    ]);

    // Branch conditions
    const [branchConditions, setBranchConditions] = useState<any[]>([]);
    const [selectedValue, setSelectedValue] = useState<string>('');
    const [showConditionSelect, setShowConditionSelect] = useState(false);

    const [localState, setLocalState] = useState({
      tag: node?.data?.tag || '',
      template: node?.data?.template || '',
      variantId: node?.data?.variantId || '',
      variables: node?.data?.variables || { name: '' },
      delayType: node?.data?.delayType || '',
      amount: node?.data?.amount || 1,
      unit: node?.data?.unit || 'days',
      date: node?.data?.date ? parseDate(node.data.date) : new Date(),
      time: node?.data?.time || '12:00',
      conditions: node?.data?.conditions || [],
      maxWaitAmount: node?.data?.maxWaitAmount || 0,
      maxWaitUnit: node?.data?.maxWaitUnit || '',
      selectedTags: node?.data?.selectedTags || [],
      buckets: node?.data?.buckets || [
        { name: 'A', weight: 50 },
        { name: 'B', weight: 50 },
      ],
      branchConditions: node?.data?.branchConditions || [],
    });

    const [showAddCondition, setShowAddCondition] = useState(
      node?.data?.conditions?.length === 0
    );
    const [currentCategory, setCurrentCategory] = useState('');
    const [currentOperator, setCurrentOperator] = useState('');
    const [currentValue, setCurrentValue] = useState('');

    // Ref to prevent infinite loop in campaign preselection
    const campaignPreselectedRef = useRef(false);

    function parseDate(dateStr: string | undefined): Date {
      if (!dateStr) return new Date();
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1;
          const year = parseInt(parts[2]);
          const date = new Date(year, month, day);
          if (!isNaN(date.getTime())) return date;
        }
      }
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? new Date() : date;
    }

    function formatDate(date: Date): string {
      return `${date.getDate().toString().padStart(2, '0')}-${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, '0')}-${date.getFullYear()}`;
    }

    // Format condition display for Multi Split and Branch
    const formatConditionDisplay = (condition: any): string => {
      if (!condition) return 'No condition';
      if (typeof condition === 'string') return condition;

      if (condition.entity && condition.field) {
        const fieldMap: Record<string, string> = {
          email_subscription: 'Email Subscription',
          email_engagement: 'Email Engagement',
          email_status: 'Email Status',
          email_interaction: 'Email Interaction',
          first_name: 'First Name',
          last_name: 'Last Name',
          email: 'Email',
        };
        const fieldLabel = fieldMap[condition.field] || condition.field;
        return `${condition.entity} → ${fieldLabel}`;
      }

      return JSON.stringify(condition);
    };

    const conditionOptions = [
      {
        category: 'Email',
        options: [
          { label: 'Email Subscription', value: 'Email:email_subscription' },
          { label: 'Email Engagement', value: 'Email:email_engagement' },
          { label: 'Email Status', value: 'Email:email_status' },
          { label: 'Email Interaction', value: 'Email:email_interaction' },
        ],
      },
      {
        category: 'Contact',
        options: [
          { label: 'First Name', value: 'Contact:first_name' },
          { label: 'Last Name', value: 'Contact:last_name' },
          { label: 'Email', value: 'Contact:email' },
        ],
      },
    ];

    const formik = useFormik({
      initialValues: {
        url: automationData.customFormUrl || '',
        tag: localState.tag,
        nodeType: node?.data?.type || '',
      },
      validationSchema: formScanSchema,
      onSubmit: async (values): Promise<void> => {
        await handleScanForm(values.url);
      },
    });

    const updateNodeData = useCallback(
      (updates: Partial<NodeData>) => {
        if (readOnly) return;
        if (node && onUpdateNode) {
          onUpdateNode({ ...node, data: { ...node.data, ...updates } });
        }
      },
      [node, onUpdateNode, readOnly]
    );

    // ==================== FETCH CAMPAIGNS ====================
    const fetchCampaigns = useCallback(async () => {
      const { token, tenantId, userId } = getAuthContext();
      if (!token) return;

      setLoadingCampaigns(true);

      try {
        const response = await fetch(
          `${BASE_URL}/v1/campaign-wizard?page=0&size=1000`,
          {
            method: 'GET',
            headers: {
              'X-Tenant-Id': tenantId,
              'X-User-Id': userId || '',
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok)
          throw new Error(`Failed to fetch campaigns: ${response.status}`);
        const data = await response.json();
        const filteredCampaigns = (data.content || []).filter(
          (campaign: Campaign) => campaign.status !== 'DRAFT'
        );
        setCampaigns(filteredCampaigns);
      } catch (error: any) {
        // console.error('Failed to fetch campaigns:', error);
        toast.error(error.message || 'Failed to load campaigns');
      } finally {
        setLoadingCampaigns(false);
      }
    }, []);

    // ==================== FETCH TEMPLATES ====================
    const fetchTemplates = useCallback(async () => {
      const { token, tenantId, userId } = getAuthContext();
      if (!token) return;

      const orgId = 'org_123456';

      try {
        const response = await fetch(
          `${BASE_URL}/v1/templates/user-define?orgId=${orgId}&page=0&size=1000`,
          {
            method: 'GET',
            headers: {
              'X-Tenant-Id': tenantId,
              'X-User-Id': userId || '',
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok)
          throw new Error(`Failed to fetch templates: ${response.status}`);
      } catch (error: any) {
        // console.error('Failed to fetch templates:', error);
        toast.error(error.message || 'Failed to load templates');
      }
    }, []);

    // ==================== PRESELECT CAMPAIGN FROM API ====================
    useEffect(() => {
      if (campaignPreselectedRef.current) return;
      if (!node?.data?.variantId || loadingCampaigns || campaigns.length === 0)
        return;

      const campaignId = node.data.variantId;
      const matchingCampaign = campaigns.find((c) => c.id === campaignId);

      if (matchingCampaign) {
        campaignPreselectedRef.current = true;
        if (selectedCampaignId !== campaignId) {
          setSelectedCampaignId(campaignId);
          setLocalState((prev) => ({
            ...prev,
            template: campaignId,
            variantId: campaignId,
          }));

          if (!readOnly) {
            updateNodeData({
              template: campaignId,
              variantId: campaignId,
            });
          }
        }
      }
    }, [
      campaigns,
      loadingCampaigns,
      node?.data?.variantId,
      selectedCampaignId,
      updateNodeData,
      readOnly,
    ]);

    useEffect(() => {
      campaignPreselectedRef.current = false;
    }, [node?.id, node?.data?.variantId]);

    // ==================== FETCH TAGS ====================
    const fetchTags = useCallback(async () => {
      const { token, tenantId, userId } = getAuthContext();
      if (!token) return;

      setLoadingTags(true);

      try {
        const response = await fetch(`${BASE_URL}/v1/audience-insights/tags`, {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const tagsData = await response.json();
          const tagsArray = Array.isArray(tagsData) ? tagsData : [];
          const uniqueTags = tagsArray.filter(
            (tag: string, index: number, self: string[]) =>
              index === self.indexOf(tag)
          );
          const tagsWithTimestamps: TagWithTimestamp[] = uniqueTags.map(
            (tag, index) => ({
              name: tag,
              createdAt: Date.now() - (uniqueTags.length - index) * 1000,
            })
          );
          tagsWithTimestamps.sort((a, b) => b.createdAt - a.createdAt);
          setAllTags(tagsWithTimestamps);
        }
      } catch (error) {
        // console.error('Failed to fetch tags:', error);
        toast.error('Failed to fetch tags');
      } finally {
        setLoadingTags(false);
      }
    }, []);

    // ==================== CREATE TAG ====================
    const handleCreateTag = async () => {
      if (readOnly) return;
      const trimmedTag = newTagName.trim();
      if (!trimmedTag) {
        toast.error('Please enter a tag name');
        return;
      }
      if (
        allTags.some((t) => t.name.toLowerCase() === trimmedTag.toLowerCase())
      ) {
        setTagValidationError('Tag already exists');
        return;
      }
      setTagValidationError('');

      const { token, tenantId, userId } = getAuthContext();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      setIsCreatingTag(true);

      try {
        const response = await fetch(`${BASE_URL}/v1/audience-insights/tags`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name: trimmedTag }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        toast.success(`Tag "${trimmedTag}" created successfully!`);
        const newTag: TagWithTimestamp = {
          name: trimmedTag,
          createdAt: Date.now(),
        };
        setAllTags((prev) => [newTag, ...prev]);
        setSelectedTags((prev) => [...prev, trimmedTag]);
        updateNodeData({
          selectedTags: [...selectedTags, trimmedTag],
          tag: [...selectedTags, trimmedTag].join(', '),
        });
        setIsAddingTag(false);
        setNewTagName('');
        setTimeout(fetchTags, 2000);
      } catch (error: any) {
        // console.error('Failed to create tag:', error);
        toast.error(error.message || 'Failed to create tag');
        fetchTags();
      } finally {
        setIsCreatingTag(false);
      }
    };

    const toggleTag = (tagName: string) => {
      if (readOnly) return;
      setSelectedTags((prev) => {
        const newSelection = prev.includes(tagName)
          ? prev.filter((t) => t !== tagName)
          : [...prev, tagName];
        updateNodeData({
          selectedTags: newSelection,
          tag: newSelection.join(', '),
        });
        return newSelection;
      });
    };

    const removeTag = (tagName: string) => {
      if (readOnly) return;
      setSelectedTags((prev) => {
        const newSelection = prev.filter((t) => t !== tagName);
        updateNodeData({
          selectedTags: newSelection,
          tag: newSelection.join(', '),
        });
        return newSelection;
      });
    };

    const toggleTagsDropdown = () => {
      if (readOnly) return;
      const newState = !isTagsDropdownOpen;
      if (!newState) {
        setIsAddingTag(false);
        setNewTagName('');
        setTagValidationError('');
        setTagSearchTerm('');
      }
      setIsTagsDropdownOpen(newState);
    };

    const filteredTags = useMemo(() => {
      if (!tagSearchTerm.trim()) return allTags;
      const term = tagSearchTerm.toLowerCase().trim();
      return allTags.filter((tag) => tag.name.toLowerCase().includes(term));
    }, [allTags, tagSearchTerm]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (readOnly) return;
        if (
          tagsDropdownRef.current &&
          !tagsDropdownRef.current.contains(
            event.target as unknown as globalThis.Node
          )
        ) {
          setIsTagsDropdownOpen(false);
          setIsAddingTag(false);
          setNewTagName('');
          setTagValidationError('');
          setTagSearchTerm('');
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [readOnly]);

    // Percentage split handlers
    const handleBucketChange = useCallback(
      (index: number, field: 'name' | 'weight', value: string | number) => {
        if (readOnly) return;
        const newBuckets = [...buckets];
        if (field === 'name') {
          newBuckets[index] = { ...newBuckets[index], name: value as string };
        } else {
          newBuckets[index] = { ...newBuckets[index], weight: value as number };
        }
        setBuckets(newBuckets);
        setLocalState((prev) => ({ ...prev, buckets: newBuckets }));
        updateNodeData({ buckets: newBuckets });
      },
      [buckets, updateNodeData, readOnly]
    );

    const handleAddBucket = useCallback(() => {
      if (readOnly) return;
      const newBuckets = [
        ...buckets,
        { name: String.fromCharCode(65 + buckets.length), weight: 0 },
      ];
      const equalWeight = Math.floor(100 / newBuckets.length);
      const remaining = 100 - equalWeight * (newBuckets.length - 1);
      newBuckets.forEach((b, i) => {
        b.weight = i === newBuckets.length - 1 ? remaining : equalWeight;
      });
      setBuckets(newBuckets);
      setLocalState((prev) => ({ ...prev, buckets: newBuckets }));
      updateNodeData({ buckets: newBuckets });
    }, [buckets, updateNodeData, readOnly]);

    const handleRemoveBucket = useCallback(
      (index: number) => {
        if (readOnly) return;
        if (buckets.length <= 2) {
          toast.warning('Minimum 2 branches required');
          return;
        }
        const newBuckets = buckets.filter((_, i) => i !== index);
        const equalWeight = Math.floor(100 / newBuckets.length);
        const remaining = 100 - equalWeight * (newBuckets.length - 1);
        newBuckets.forEach((b, i) => {
          b.weight = i === newBuckets.length - 1 ? remaining : equalWeight;
        });
        setBuckets(newBuckets);
        setLocalState((prev) => ({ ...prev, buckets: newBuckets }));
        updateNodeData({ buckets: newBuckets });
      },
      [buckets, updateNodeData, readOnly]
    );

    // Branch condition handlers
    const addBranchCondition = useCallback(() => {
      if (readOnly) return;
      if (!selectedValue) return;
      const [entity, field] = selectedValue.split(':');
      const newCondition = {
        entity,
        field,
        operator: 'EXISTS',
        value: null,
        label: `Condition ${branchConditions.length + 1}`,
      };
      const newConditions = [...branchConditions, newCondition];
      setBranchConditions(newConditions);
      setLocalState((prev) => ({ ...prev, branchConditions: newConditions }));
      setSelectedValue('');
      setShowConditionSelect(false);
      updateNodeData({ branchConditions: newConditions });
    }, [selectedValue, branchConditions, updateNodeData, readOnly]);

    const removeBranchCondition = useCallback(
      (index: number) => {
        if (readOnly) return;
        const newConditions = branchConditions.filter((_, i) => i !== index);
        setBranchConditions(newConditions);
        setLocalState((prev) => ({ ...prev, branchConditions: newConditions }));
        updateNodeData({ branchConditions: newConditions });
      },
      [branchConditions, updateNodeData, readOnly]
    );

    // SYNC EFFECT – conditions for Trigger come from Redux
    useEffect(() => {
      setLocalTrigger(automationData.trigger || '');
      setCondition('');

      if (node) {
        if (
          node.data.tag &&
          typeof node.data.tag === 'string' &&
          node.data.tag.includes(',')
        ) {
          const parsedTags = node.data.tag
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean);
          setSelectedTags(parsedTags);
        } else if (node.data.tag && typeof node.data.tag === 'string') {
          setSelectedTags([node.data.tag]);
        } else if (node.data.selectedTags) {
          setSelectedTags(node.data.selectedTags);
        } else {
          setSelectedTags([]);
        }

        if (node.data.type === 'percent') {
          const nodeBuckets = node.data.buckets || [
            { name: 'A', weight: 50 },
            { name: 'B', weight: 50 },
          ];
          setBuckets(nodeBuckets);
        }

        if (node.data.type === 'branch' || node.data.isBranch) {
          const nodeBranchConditions = node.data.branchConditions || [];
          setBranchConditions(nodeBranchConditions);
        }

        const conditionsToUse =
          node.type === 'customHome' || node.id === 'home'
            ? automationData.conditions || []
            : node.data.conditions || [];

        setLocalState((prev) => ({
          tag: node.data.tag || '',
          template: node.data.template || '',
          variantId: node.data.variantId || '',
          variables: node.data.variables || { name: '' },
          delayType: node.data.delayType || '',
          amount: node.data.amount || 1,
          unit: node.data.unit || 'days',
          date: node.data.date ? parseDate(node.data.date) : new Date(),
          time: node.data.time || '12:00',
          conditions: conditionsToUse,
          maxWaitAmount: node.data.maxWaitAmount || 0,
          maxWaitUnit: node.data.maxWaitUnit || '',
          selectedTags: node.data.selectedTags || [],
          buckets: node.data.buckets || [
            { name: 'A', weight: 50 },
            { name: 'B', weight: 50 },
          ],
          branchConditions: node.data.branchConditions || [],
        }));

        setShowAddCondition(conditionsToUse.length === 0);

        formik.setFieldValue('tag', node.data.tag || '');
        formik.setFieldValue('nodeType', node.data.type || '');
      }
    }, [node, automationData.trigger, automationData.conditions]);

    useEffect(() => {
      if (node?.data?.type === 'send') {
        fetchCampaigns();
        fetchTemplates();
      }
      if (node?.data?.type === 'tags') fetchTags();
    }, [node?.data?.type, fetchCampaigns, fetchTemplates, fetchTags]);

    // ==================== PREVIEW EMAIL ====================
    const handlePreviewEmail = useCallback(async () => {
      const campaignId =
        selectedCampaignId ||
        localState.template ||
        node?.data?.variantId ||
        node?.data?.template;

      if (!campaignId) {
        toast.error(
          'No campaign selected. Please select a campaign from the dropdown.'
        );
        return;
      }

      const selectedCampaign = campaigns.find((c) => c.id === campaignId);
      if (!selectedCampaign) {
        toast.error('Selected campaign not found. Please try again.');
        return;
      }

      const templateId = selectedCampaign.templateId;
      if (!templateId) {
        toast.error('No template associated with this campaign');
        return;
      }

      setPreviewHtml('');

      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      setPreviewLoading(true);

      try {
        const response = await fetch(
          `${BASE_URL}/v1/templates/user-define/${templateId}`,
          {
            method: 'GET',
            headers: {
              'X-Tenant-Id': tenantId,
              'X-User-Id': userId || '',
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const templateData = await response.json();

        let htmlContent = '';

        if (templateData.html) {
          htmlContent = templateData.html;
        } else if (templateData.blocks && Array.isArray(templateData.blocks)) {
          const rawHtmlBlocks = templateData.blocks.filter(
            (block: any) => block.type === 'rawHtml'
          );
          htmlContent = rawHtmlBlocks.map((block: any) => block.text).join('\n');

          if (!htmlContent) {
            const paragraphBlocks = templateData.blocks.filter(
              (block: any) => block.type === 'paragraph'
            );
            htmlContent = paragraphBlocks
              .map((block: any) => block.text)
              .join('\n');
          }
        } else if (templateData.content) {
          htmlContent = templateData.content;
        }

        if (templateData.styles && htmlContent) {
          const hasStyleTag = /<style[^>]*>/.test(htmlContent);

          if (hasStyleTag) {
            htmlContent = htmlContent.replace(
              /<style[^>]*>([\s\S]*?)<\/style>/,
              `<style>${templateData.styles}</style>`
            );
          } else {
            if (/<head[^>]*>/.test(htmlContent)) {
              htmlContent = htmlContent.replace(
                /<head[^>]*>/,
                `<head><style>${templateData.styles}</style>`
              );
            } else {
              htmlContent = `<style>${templateData.styles}</style>\n${htmlContent}`;
            }
          }
        }

        if (!htmlContent) {
          toast.warning(
            'No content available for preview. The template might be empty.'
          );
          setPreviewLoading(false);
          return;
        }

        if (node && onUpdateNode && selectedCampaignId && !readOnly) {
          const updatedNode = {
            ...node,
            data: {
              ...node.data,
              template: selectedCampaignId,
              variantId: selectedCampaignId,
            },
          };
          onUpdateNode(updatedNode);
        }

        setPreviewHtml(htmlContent);
        setIsPreviewModalOpen(true);
      } catch (error: any) {
        // console.error('❌ Failed to fetch template preview:', error);
        toast.error(error.message || 'Failed to load template preview');
      } finally {
        setPreviewLoading(false);
      }
    }, [
      selectedCampaignId,
      localState.template,
      node,
      onUpdateNode,
      campaigns,
      readOnly,
    ]);

    const handleTriggerChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (readOnly) return;
        const newTrigger = e.target.value;
        setLocalTrigger(newTrigger);
        dispatch(updateAutomationData({ trigger: newTrigger }));
        setCondition('');
        formik.resetForm();
        setScanError('');
      },
      [dispatch, readOnly]
    );

    const handleScanForm = useCallback(
      async (url: string) => {
        if (readOnly) return;
        setScanError('');
        try {
          formik.setFieldValue('url', url);
        } catch (error) {
          setScanError(
            'Failed to scan form. Please check the URL and try again.'
          );
        }
      },
      [formik, readOnly]
    );

    const handleCampaignChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (readOnly) return;
        const campaignId = e.target.value;

        setPreviewHtml('');
        setIsPreviewModalOpen(false);

        setSelectedCampaignId(campaignId);
        setLocalState((prev) => ({
          ...prev,
          template: campaignId,
          variantId: campaignId,
        }));

        updateNodeData({
          template: campaignId,
          variantId: campaignId,
          variables: localState.variables,
        });
      },
      [updateNodeData, localState.variables, readOnly]
    );

    const handleDelayTypeChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (readOnly) return;
        const value = e.target.value as 'fixed' | 'specific' | '';
        const newState = { ...localState, delayType: value };

        if (value === 'fixed') {
          newState.amount = localState.amount || 1;
          newState.unit = localState.unit || 'days';
          newState.date = new Date();
          newState.time = '';
        } else if (value === 'specific') {
          newState.date = localState.date || new Date();
          newState.time = localState.time || '12:00';
          newState.amount = 1;
          newState.unit = 'days';
        } else {
          newState.amount = 1;
          newState.unit = 'days';
          newState.date = new Date();
          newState.time = '';
        }

        setLocalState(newState);
        updateNodeData({
          delayType: value === '' ? undefined : value,
          amount: newState.amount,
          unit: newState.unit,
          date: newState.date ? formatDate(newState.date) : undefined,
          time: newState.time,
        });
      },
      [updateNodeData, localState, readOnly]
    );

    const handleAmountChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (readOnly) return;
        const value = parseInt(e.target.value) || 1;
        setLocalState((prev) => ({ ...prev, amount: value }));
        updateNodeData({
          amount: value,
          delayType:
            localState.delayType === 'fixed' ||
              localState.delayType === 'specific'
              ? localState.delayType
              : undefined,
          unit: localState.unit,
        });
      },
      [updateNodeData, localState, readOnly]
    );

    const handleUnitChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (readOnly) return;
        const value = e.target.value as UnitType;
        setLocalState((prev) => ({ ...prev, unit: value }));
        updateNodeData({
          unit: value,
          delayType:
            localState.delayType === 'fixed' ||
              localState.delayType === 'specific'
              ? localState.delayType
              : undefined,
          amount: localState.amount || 1,
        });
      },
      [updateNodeData, localState, readOnly]
    );

    const handleDateChange = useCallback(
      (date: Date | null) => {
        if (readOnly) return;
        if (date && node && onUpdateNode) {
          const formattedDate = formatDate(date);
          setLocalState((prev) => ({ ...prev, date }));
          updateNodeData({
            date: formattedDate,
            time: localState.time,
            delayType:
              localState.delayType === 'fixed' ||
                localState.delayType === 'specific'
                ? localState.delayType
                : undefined,
          });
        }
      },
      [updateNodeData, localState, node, onUpdateNode, readOnly]
    );

    const handleTimeChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (readOnly) return;
        const value = e.target.value;
        if (node && onUpdateNode) {
          setLocalState((prev) => ({ ...prev, time: value }));
          updateNodeData({
            time: value,
            date: localState.date ? formatDate(localState.date) : undefined,
            delayType:
              localState.delayType === 'fixed' ||
                localState.delayType === 'specific'
                ? localState.delayType
                : undefined,
          });
        }
      },
      [updateNodeData, localState, node, onUpdateNode, readOnly]
    );

    const handleMaxWaitAmountChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (readOnly) return;
        const value = parseInt(e.target.value) || 0;
        setLocalState((prev) => ({ ...prev, maxWaitAmount: value }));
        updateNodeData({
          maxWaitAmount: value,
          maxWaitUnit: localState.maxWaitUnit,
        });
      },
      [updateNodeData, localState.maxWaitUnit, readOnly]
    );

    const handleMaxWaitUnitChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (readOnly) return;
        const value = e.target.value;
        setLocalState((prev) => ({ ...prev, maxWaitUnit: value }));
        updateNodeData({
          maxWaitUnit: value,
          maxWaitAmount: localState.maxWaitAmount,
        });
      },
      [updateNodeData, localState.maxWaitAmount, readOnly]
    );

    if (!node) {
      return (
        <div className="bg-white">
          <div className="text-sm font-medium">Select a step</div>
          <div className="text-gray-500 mt-2">
            Click a node to edit its settings.
          </div>
        </div>
      );
    }

    // Read-only view
    if (readOnly) {
      return (
        <>
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-2 pb-4 border-b border-gray-300">
              <div className="font-semibold text-[#3F5265] text-[16px]">
                {node.data.label}
              </div>
            </div>

            {node.type === 'customHome' && (
              <>
                <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                  TRIGGER TYPE
                </div>
                <div className="text-[14px] text-[#3F5265] mb-4">
                  {localTrigger || 'No Trigger Selected'}
                </div>

                {localTrigger === 'form_submission' &&
                  localState.conditions.length > 0 && (
                    <>
                      <div className="text-[12px] mb-2 text-[#9FA9B2]">
                        CONDITIONS
                      </div>
                      {localState.conditions.map((cond, index) => (
                        <div key={index}>
                          {index > 0 && (
                            <div className="text-[#9FA9B2] text-[12px] font-medium py-2">
                              AND
                            </div>
                          )}
                          <div className="flex items-center gap-2 bg-[#F5F8FA] border border-[#DAE4F2] rounded p-2">
                            <div className="flex-1">
                              <span className="text-[#3F5265] text-[13px] font-medium">
                                {cond.category === 'email_subscription' &&
                                  'Email Subscription'}
                                {cond.category === 'email_engagement' &&
                                  'Email Engagement'}
                                {cond.category === 'email_status' &&
                                  'Email Status'}
                                {cond.category === 'email_interaction' &&
                                  'Email Interaction'}
                                {cond.category === 'first_name' && 'First Name'}
                                {cond.category === 'last_name' && 'Last Name'}
                                {cond.category === 'email' && 'Email'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
              </>
            )}

            {node.type !== 'customHome' && (
              <>
                {/* ===== TAGS ===== */}
                {node.data.type === 'tags' && (
                  <>
                    <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                      ADD TAGS
                    </div>
                    {selectedTags.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedTags.map((tagName) => (
                          <span
                            key={tagName}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-[#E0F8FB] text-[#00899E] rounded-full text-xs"
                          >
                            {tagName}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[14px] text-[#3F5265]">
                        No tags selected
                      </div>
                    )}
                  </>
                )}

                {/* ===== SEND EMAIL ===== */}
                {node.data.type === 'send' && (
                  <>
                    <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                      CAMPAIGN
                    </div>
                    {(() => {
                      const campaignId =
                        selectedCampaignId ||
                        localState.template ||
                        node?.data?.variantId ||
                        node?.data?.template;
                      const selectedCampaign = campaigns.find(
                        (c) => c.id === campaignId
                      );

                      if (selectedCampaign) {
                        const templateData = selectedCampaign.template;
                        const hasThumbnail =
                          templateData?.thumbnailUrl &&
                          templateData.thumbnailUrl !== '';

                        return (
                          <>
                            {hasThumbnail ? (
                              <img
                                src={templateData.thumbnailUrl ?? undefined}
                                alt="Campaign Template Preview"
                                className="w-full mb-2 rounded"
                                onError={(e) => {
                                  (
                                    e.target as HTMLImageElement
                                  ).style.display = 'none';
                                  const parent = (
                                    e.target as HTMLImageElement
                                  ).parentElement;
                                  if (parent) {
                                    const placeholder =
                                      document.createElement('div');
                                    placeholder.className =
                                      'w-full h-48 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-sm mb-2';
                                    placeholder.textContent = 'No Image';
                                    parent.appendChild(placeholder);
                                  }
                                }}
                              />
                            ) : (
                              <div className="w-full h-48 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-sm mb-2">
                                No Image
                              </div>
                            )}

                            <div className="mb-3 p-2 bg-gray-50 rounded text-xs">
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-gray-600">
                                  Campaign:
                                </span>
                                <span className="text-gray-800">
                                  {selectedCampaign.title}
                                </span>
                              </div>

                              <div className="flex justify-between items-center mt-1">
                                <span className="font-medium text-gray-600">
                                  Status:
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded text-xs ${selectedCampaign.status === 'SCHEDULED'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : selectedCampaign.status === 'DRAFT'
                                      ? 'bg-gray-100 text-gray-800'
                                      : selectedCampaign.status === 'ACTIVE'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                >
                                  {selectedCampaign.status}
                                </span>
                              </div>
                            </div>

                            <button
                              onClick={handlePreviewEmail}
                              disabled={previewLoading || !campaignId}
                              className={`w-full bg-[#00A4BD] text-white rounded px-3 py-2 text-[14px] font-semibold mb-4 ${previewLoading || !campaignId
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                                }`}
                            >
                              {previewLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                  <svg
                                    className="animate-spin h-4 w-4"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                      fill="none"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                                    />
                                  </svg>
                                  Loading...
                                </span>
                              ) : (
                                'Preview Email'
                              )}
                            </button>
                          </>
                        );
                      }

                      if (campaignId) {
                        return (
                          <>
                            <div className="text-[14px] text-[#3F5265] mb-2">
                              Campaign ID: {campaignId}
                            </div>
                            <button
                              onClick={handlePreviewEmail}
                              disabled={previewLoading}
                              className={`w-full bg-[#00A4BD] text-white rounded px-3 py-2 text-[14px] font-semibold mb-4 ${previewLoading
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                                }`}
                            >
                              {previewLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                  <svg
                                    className="animate-spin h-4 w-4"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                      fill="none"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                                    />
                                  </svg>
                                  Loading...
                                </span>
                              ) : (
                                'Preview Email'
                              )}
                            </button>
                          </>
                        );
                      }

                      return (
                        <div className="text-[14px] text-[#3F5265]">
                          No campaign selected
                        </div>
                      );
                    })()}
                  </>
                )}

                {/* ===== TIME DELAY ===== */}
                {node.data.type === 'time' && (
                  <>
                    <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                      DELAY TYPE
                    </div>
                    <div className="text-[14px] text-[#3F5265] mb-4">
                      {localState.delayType === 'fixed' &&
                        'Fixed time from user action'}
                      {localState.delayType === 'specific' &&
                        'Specific date & time'}
                      {!localState.delayType && 'Not set'}
                    </div>

                    {localState.delayType === 'fixed' && (
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <div className="text-[12px] mb-1 text-[#9FA9B2]">
                            AMOUNT
                          </div>
                          <div className="text-[14px] text-[#3F5265]">
                            {localState.amount}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="text-[12px] mb-1 text-[#9FA9B2]">
                            UNIT
                          </div>
                          <div className="text-[14px] text-[#3F5265]">
                            {localState.unit}
                          </div>
                        </div>
                      </div>
                    )}

                    {localState.delayType === 'specific' && (
                      <>
                        <div className="text-[12px] mb-1 text-[#9FA9B2]">
                          DATE
                        </div>
                        <div className="text-[14px] text-[#3F5265] mb-3">
                          {localState.date
                            ? formatDate(localState.date)
                            : '-'}
                        </div>
                        <div className="text-[12px] mb-1 text-[#9FA9B2]">
                          TIME
                        </div>
                        <div className="text-[14px] text-[#3F5265]">
                          {localState.time || '-'}
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* ===== WAIT UNTIL ===== */}
                {node.data.type === 'wait' && (
                  <div className="rounded border border-[#DAE4F2] p-2 mt-4">
                    <div className="text-[14px] mb-4 text-[#3F5265] font-medium">
                      Max wait
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <div className="text-[12px] mb-1 text-[#9FA9B2]">
                          AMOUNT
                        </div>
                        <div className="text-[14px] text-[#3F5265]">
                          {localState.maxWaitAmount || 0}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-[12px] mb-1 text-[#9FA9B2]">
                          UNIT
                        </div>
                        <div className="text-[14px] text-[#3F5265]">
                          {localState.maxWaitUnit || '-'}
                        </div>
                      </div>
                    </div>
                    {localState.conditions &&
                      localState.conditions.length > 0 && (
                        <>
                          <div className="text-[12px] mb-1 mt-3 text-[#9FA9B2]">
                            CONDITIONS
                          </div>
                          {localState.conditions.map((cond, index) => (
                            <div
                              key={index}
                              className="text-[13px] text-[#3F5265] bg-[#F5F8FA] p-1 rounded mb-1"
                            >
                              {cond.category} {cond.operator} {cond.value}
                            </div>
                          ))}
                        </>
                      )}
                  </div>
                )}

                {/* ===== PERCENTAGE SPLIT ===== */}
                {node.data.type === 'percent' && (
                  <>
                    <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                      PERCENTAGE SPLIT CONFIGURATION
                    </div>
                    <div className="space-y-3">
                      {buckets.map((bucket, index) => (
                        <div
                          key={index}
                          className="bg-[#F5F8FA] border border-[#DAE4F2] rounded p-3"
                        >
                          <div className="flex justify-between items-center mb-2">
                            <div className="text-[12px] font-medium text-[#3F5265]">
                              Branch{' '}
                              {bucket.name || String.fromCharCode(65 + index)}
                            </div>
                          </div>
                          <div className="text-[14px] text-[#3F5265]">
                            Name: {bucket.name || '-'}
                          </div>
                          <div className="text-[14px] text-[#3F5265]">
                            Weight: {bucket.weight || 0}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* ===== MULTI SPLIT ===== */}
                {(node.data.type === 'multi' || node.data.isMultiSplit) &&
                  node.data.multiSplitRules && (
                    <>
                      <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                        MULTI SPLIT RULES
                      </div>
                      <div className="space-y-3">
                        {node.data.multiSplitRules.map((rule, index) => (
                          <div
                            key={rule.id}
                            className="bg-[#F5F8FA] border border-[#DAE4F2] rounded p-3"
                          >
                            <div className="flex justify-between items-center mb-2">
                              <div className="text-[12px] font-medium text-[#3F5265]">
                                {rule.label || `Rule ${index + 1}`}
                              </div>
                            </div>
                            <div className="text-[13px] text-[#3F5265] bg-white p-2 rounded border border-gray-100">
                              {rule.condition
                                ? formatConditionDisplay(rule.condition)
                                : 'Default'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                {/* ===== BRANCH (True/False) - Read Only ===== */}
                {(node.data.type === 'branch' || node.data.isBranch) && (
                  <>
                    <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                      BRANCH CONDITIONS
                    </div>
                    {branchConditions.length > 0 ? (
                      <div className="space-y-2">
                        {branchConditions.map((cond, index) => (
                          <div
                            key={index}
                            className="bg-gray-50 border border-gray-200 rounded p-3"
                          >
                            <div className="text-sm font-medium text-gray-700">
                              Condition {index + 1}
                            </div>
                            <div className="text-xs text-blue-600 mt-1 font-medium">
                              {formatConditionDisplay(cond)}
                            </div>
                            {index < branchConditions.length - 1 && (
                              <div className="text-xs font-bold text-gray-500 mt-2">
                                {node.data.conditionLogic || 'AND'}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[14px] text-[#3F5265]">
                        No conditions defined
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Preview Modal (separate component) */}
          <EmailPreviewModal
            isOpen={isPreviewModalOpen}
            onClose={() => setIsPreviewModalOpen(false)}
            previewHtml={previewHtml}
            previewLoading={previewLoading}
          />
        </>
      );
    }

    // Editable view
    return (
      <div className="flex flex-col">
        <div className="flex justify-between items-center mb-2 pb-4 border-b border-gray-300">
          <div className="font-semibold text-[#3F5265] text-[16px]">
            {node.data.label}
          </div>
        </div>

        {node.type === 'customHome' && (
          <>
            <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
              TRIGGER TYPE
            </div>
            <select
              value={localTrigger}
              onChange={handleTriggerChange}
              className="border rounded p-2 mb-5 w-full"
            >
              <option value="">Select Trigger</option>
              <option value="form_submission">Form Submission</option>
              <option value="new_subscriber">New Subscriber</option>
              <option value="email_opened">Email Opened</option>
              <option value="link_clicked">Link Clicked</option>
              <option value="scheduled_date_time">Scheduled Date/Time</option>
            </select>

            {localTrigger === 'form_submission' && (
              <>
                <div className="text-[12px] mb-2 text-[#9FA9B2]">
                  CONDITIONS
                </div>

                <select
                  value={condition}
                  onChange={(e) => {
                    const newCondition = e.target.value;
                    if (newCondition) {
                      const isAlreadyUsed = localState.conditions.some(
                        (cond: any) => cond.category === newCondition
                      );

                      if (isAlreadyUsed) {
                        toast.warning(
                          'This condition has already been added. Please remove the existing one before adding it again.',
                          {
                            position: 'top-right',
                            autoClose: 3000,
                            theme: 'colored',
                            style: {
                              backgroundColor: '#fef3c7',
                              color: '#d97706',
                            },
                          }
                        );
                        setCondition('');
                        return;
                      }

                      const newConditions = [
                        ...localState.conditions,
                        {
                          category: newCondition,
                          operator: 'EXISTS',
                          value: '',
                        },
                      ];
                      setLocalState((prev) => ({
                        ...prev,
                        conditions: newConditions,
                      }));
                      updateNodeData({ conditions: newConditions });
                      dispatch(
                        updateAutomationData({ conditions: newConditions })
                      );
                      setCondition('');
                    }
                  }}
                  className="border rounded p-2 mb-2 w-full"
                >
                  <option value="">Select Condition</option>
                  <option
                    disabled
                    style={{ fontWeight: 'bold', color: '#3F5265' }}
                  >
                    Email
                  </option>
                  {[
                    {
                      value: 'email_subscription',
                      label: 'Email Subscription',
                    },
                    { value: 'email_engagement', label: 'Email Engagement' },
                    { value: 'email_status', label: 'Email Status' },
                    {
                      value: 'email_interaction',
                      label: 'Email Interaction',
                    },
                  ].map((opt) => {
                    const isUsed = localState.conditions.some(
                      (cond: any) => cond.category === opt.value
                    );
                    return (
                      <option
                        key={opt.value}
                        value={opt.value}
                        disabled={isUsed}
                        className={isUsed ? 'text-gray-400 bg-gray-50' : ''}
                      >
                        {opt.label} {isUsed ? '(Already Added)' : ''}
                      </option>
                    );
                  })}
                  <option
                    disabled
                    style={{ fontWeight: 'bold', color: '#3F5265' }}
                  >
                    Contact
                  </option>
                  {[
                    { value: 'first_name', label: 'First Name' },
                    { value: 'last_name', label: 'Last Name' },
                    { value: 'email', label: 'Email' },
                  ].map((opt) => {
                    const isUsed = localState.conditions.some(
                      (cond: any) => cond.category === opt.value
                    );
                    return (
                      <option
                        key={opt.value}
                        value={opt.value}
                        disabled={isUsed}
                        className={isUsed ? 'text-gray-400 bg-gray-50' : ''}
                      >
                        {opt.label} {isUsed ? '(Already Added)' : ''}
                      </option>
                    );
                  })}
                </select>

                {localState.conditions.map((cond: any, index: number) => (
                  <div key={index}>
                    {index > 0 && (
                      <div className="text-[#9FA9B2] text-[12px] font-medium py-2">
                        AND
                      </div>
                    )}
                    <div className="flex items-center gap-2 bg-[#F5F8FA] border border-[#DAE4F2] rounded p-2">
                      <div className="flex-1">
                        <span className="text-[#3F5265] text-[13px] font-medium">
                          {cond.category === 'email_subscription' &&
                            'Email Subscription'}
                          {cond.category === 'email_engagement' &&
                            'Email Engagement'}
                          {cond.category === 'email_status' && 'Email Status'}
                          {cond.category === 'email_interaction' &&
                            'Email Interaction'}
                          {cond.category === 'first_name' && 'First Name'}
                          {cond.category === 'last_name' && 'Last Name'}
                          {cond.category === 'email' && 'Email'}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          const newConditions = localState.conditions.filter(
                            (_: any, i: number) => i !== index
                          );
                          setLocalState((prev) => ({
                            ...prev,
                            conditions: newConditions,
                          }));
                          updateNodeData({ conditions: newConditions });
                          dispatch(
                            updateAutomationData({
                              conditions: newConditions,
                            })
                          );
                        }}
                        className="text-[#FF6B4A] hover:text-red-700"
                      >
                        <MdCloseComponent />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {node.type !== 'customHome' && (
          <>
            {/* ===== TAGS ===== */}
            {node.data.type === 'tags' && (
              <>
                <label className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                  ADD TAGS
                </label>

                {selectedTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedTags.map((tagName) => (
                      <span
                        key={tagName}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[#E0F8FB] text-[#00899E] rounded-full text-xs"
                      >
                        {tagName}
                        <button
                          type="button"
                          onClick={() => removeTag(tagName)}
                          className="hover:text-red-600"
                        >
                          <FiXComponent size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="relative" ref={tagsDropdownRef}>
                  <button
                    type="button"
                    onClick={toggleTagsDropdown}
                    className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white flex justify-between items-center"
                  >
                    <span className="text-gray-600 text-sm">
                      {selectedTags.length > 0
                        ? `${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''
                        } selected`
                        : 'Select tags...'}
                    </span>
                    <FiChevronDownComponent
                      size={20}
                      className={`transition-transform ${isTagsDropdownOpen ? 'rotate-180' : ''
                        }`}
                    />
                  </button>

                  {isTagsDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto">
                      <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
                        <input
                          type="text"
                          value={tagSearchTerm}
                          onChange={(e) => setTagSearchTerm(e.target.value)}
                          placeholder="Search tags..."
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {loadingTags ? (
                        <div className="p-3 text-center text-gray-500">
                          Loading tags...
                        </div>
                      ) : (
                        <>
                          {!isAddingTag ? (
                            <div
                              className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                              onClick={() => {
                                setIsAddingTag(true);
                                setTagValidationError('');
                              }}
                            >
                              <span className="text-blue-600 font-medium text-sm flex items-center gap-2">
                                <span className="text-blue-500 text-lg">
                                  +
                                </span>{' '}
                                Add Tag
                              </span>
                            </div>
                          ) : (
                            <div className="px-3 py-2 bg-blue-50 border-b border-blue-200">
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={newTagName}
                                  onChange={(e) => {
                                    setNewTagName(e.target.value);
                                    if (tagValidationError)
                                      setTagValidationError('');
                                  }}
                                  placeholder="Enter tag name..."
                                  className={`flex-1 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${tagValidationError
                                    ? 'border-red-500'
                                    : 'border-blue-300'
                                    }`}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleCreateTag();
                                    if (e.key === 'Escape') {
                                      setIsAddingTag(false);
                                      setNewTagName('');
                                      setTagValidationError('');
                                    }
                                  }}
                                  disabled={isCreatingTag}
                                />
                                <button
                                  type="button"
                                  onClick={handleCreateTag}
                                  disabled={
                                    isCreatingTag || !newTagName.trim()
                                  }
                                  className={`p-1 rounded hover:bg-green-100 ${isCreatingTag || !newTagName.trim()
                                    ? 'opacity-50 cursor-not-allowed'
                                    : ''
                                    }`}
                                >
                                  <FiCheckComponent
                                    size={18}
                                    className="text-green-600"
                                  />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsAddingTag(false);
                                    setNewTagName('');
                                    setTagValidationError('');
                                  }}
                                  className="p-1 rounded hover:bg-red-100"
                                >
                                  <FiCrossComponent
                                    size={18}
                                    className="text-red-600"
                                  />
                                </button>
                              </div>
                              {tagValidationError && (
                                <div className="text-red-500 text-xs mt-1">
                                  ⚠️ {tagValidationError}
                                </div>
                              )}
                            </div>
                          )}

                          {filteredTags.length === 0 && !isAddingTag ? (
                            <div className="p-3 text-center text-gray-500">
                              No tags available
                            </div>
                          ) : (
                            filteredTags.map((tagObj) => {
                              const isChecked = selectedTags.includes(
                                tagObj.name
                              );
                              return (
                                <label
                                  key={tagObj.name}
                                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleTag(tagObj.name)}
                                    className="mr-2 w-4 h-4 text-teal-600 rounded"
                                  />
                                  <span className="text-sm">
                                    {tagObj.name}
                                  </span>
                                  {isChecked && (
                                    <span className="ml-auto text-xs text-purple-600">
                                      ✓
                                    </span>
                                  )}
                                </label>
                              );
                            })
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ===== SEND EMAIL ===== */}
            {node.data.type === 'send' && (
              <>
                <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                  CAMPAIGN
                </div>

                {loadingCampaigns ? (
                  <div className="flex justify-center items-center py-4">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                      <p className="mt-2 text-xs text-gray-500">
                        Loading campaigns...
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      value={selectedCampaignId || localState.template || ''}
                      onChange={handleCampaignChange}
                      className="border rounded p-2 mb-5 w-full appearance-none bg-white pr-8"
                    >
                      <option value="">Select Campaign</option>
                      {campaigns
                        .filter((campaign) => campaign.status !== 'DRAFT')
                        .map((campaign) => (
                          <option key={campaign.id} value={campaign.id}>
                            {campaign.title} ({campaign.status})
                          </option>
                        ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg
                        className="fill-current h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                )}

                {(selectedCampaignId || localState.template) && (
                  <>
                    {(() => {
                      const selectedCampaign = campaigns.find(
                        (c) =>
                          c.id === (selectedCampaignId || localState.template)
                      );

                      if (!selectedCampaign) return null;

                      const templateData = selectedCampaign.template;
                      const hasThumbnail =
                        templateData?.thumbnailUrl &&
                        templateData.thumbnailUrl !== '';

                      return (
                        <>
                          {hasThumbnail ? (
                            <img
                              src={templateData.thumbnailUrl ?? undefined}
                              alt="Campaign Template Preview"
                              className="w-full mb-2 rounded"
                              onError={(e) => {
                                (
                                  e.target as HTMLImageElement
                                ).style.display = 'none';
                                const parent = (e.target as HTMLImageElement)
                                  .parentElement;
                                if (parent) {
                                  const placeholder =
                                    document.createElement('div');
                                  placeholder.className =
                                    'w-full h-48 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-sm mb-2';
                                  placeholder.textContent = 'No Image';
                                  parent.appendChild(placeholder);
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-48 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-sm mb-2">
                              No Image
                            </div>
                          )}

                          <div className="mb-3 p-2 bg-gray-50 rounded text-xs">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-gray-600">
                                Campaign:
                              </span>
                              <span className="text-gray-800">
                                {selectedCampaign.title}
                              </span>
                            </div>

                            <div className="flex justify-between items-center mt-1">
                              <span className="font-medium text-gray-600">
                                Status:
                              </span>
                              <span
                                className={`px-2 py-0.5 rounded text-xs ${selectedCampaign.status === 'SCHEDULED'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : selectedCampaign.status === 'DRAFT'
                                    ? 'bg-gray-100 text-gray-800'
                                    : selectedCampaign.status === 'ACTIVE'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                              >
                                {selectedCampaign.status}
                              </span>
                            </div>
                          </div>

                          <button
                            onClick={handlePreviewEmail}
                            disabled={previewLoading || !selectedCampaignId}
                            className={`w-full bg-[#00A4BD] text-white rounded px-3 py-2 text-[14px] font-semibold mb-4 ${previewLoading || !selectedCampaignId
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                              }`}
                          >
                            {previewLoading ? (
                              <span className="flex items-center justify-center gap-2">
                                <svg
                                  className="animate-spin h-4 w-4"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                                  />
                                </svg>
                                Loading...
                              </span>
                            ) : (
                              'Preview Email'
                            )}
                          </button>
                        </>
                      );
                    })()}
                  </>
                )}
              </>
            )}

            {/* ===== TIME DELAY ===== */}
            {node.data.type === 'time' && (
              <>
                <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                  DELAY TYPE
                </div>
                <select
                  value={localState.delayType}
                  onChange={handleDelayTypeChange}
                  className="border rounded p-2 mb-5 w-full"
                >
                  <option value="">Select</option>
                  <option value="fixed">Fixed time from user action</option>
                  <option value="specific">Specific date & time</option>
                </select>

                {localState.delayType === 'fixed' && (
                  <div className="flex gap-2">
                    <div>
                      <div className="text-[12px] mb-2 text-[#9FA9B2]">
                        AMOUNT
                      </div>
                      <input
                        type="number"
                        min="1"
                        value={localState.amount}
                        onChange={handleAmountChange}
                        className="border rounded p-2 w-full"
                      />
                    </div>
                    <div>
                      <div className="text-[12px] mb-2 text-[#9FA9B2]">
                        UNIT
                      </div>
                      <select
                        value={localState.unit}
                        onChange={handleUnitChange}
                        className="border rounded p-2 w-full"
                      >
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                  </div>
                )}

                {localState.delayType === 'specific' && (
                  <>
                    <div className="text-[12px] mb-2 text-[#9FA9B2]">
                      DATE
                    </div>
                    <DatePicker
                      selected={localState.date}
                      onChange={handleDateChange}
                      dateFormat="dd-MM-yyyy"
                      className="border rounded p-2 w-full mb-4"
                      minDate={new Date()}
                    />
                    <div className="text-[12px] mb-2 text-[#9FA9B2]">
                      TIME
                    </div>
                    <input
                      type="time"
                      value={localState.time}
                      onChange={handleTimeChange}
                      className="border rounded p-2 w-full"
                    />
                  </>
                )}
              </>
            )}

            {/* ===== WAIT UNTIL ===== */}
            {node.data.type === 'wait' && (
              <div className="rounded border border-[#DAE4F2] p-2 mt-4">
                <div className="text-[14px] mb-4 text-[#3F5265] font-medium">
                  Max wait
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <div className="text-[12px] mb-2 text-[#9FA9B2]">
                      AMOUNT
                    </div>
                    <input
                      type="number"
                      min="0"
                      value={localState.maxWaitAmount}
                      onChange={handleMaxWaitAmountChange}
                      className="border rounded p-2 w-full"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="text-[12px] mb-2 text-[#9FA9B2]">
                      UNIT
                    </div>
                    <select
                      value={localState.maxWaitUnit}
                      onChange={handleMaxWaitUnitChange}
                      className="border rounded p-2 w-full"
                    >
                      <option value="">Select unit</option>
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* ===== PERCENTAGE SPLIT ===== */}
            {node.data.type === 'percent' && (
              <>
                <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                  PERCENTAGE SPLIT CONFIGURATION
                </div>
                <div className="space-y-3">
                  {buckets.map((bucket, index) => (
                    <div
                      key={index}
                      className="bg-[#F5F8FA] border border-[#DAE4F2] rounded p-3"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="text-[12px] font-medium text-[#3F5265]">
                          Branch{' '}
                          {bucket.name || String.fromCharCode(65 + index)}
                        </div>
                        {buckets.length > 2 && (
                          <button
                            onClick={() => handleRemoveBucket(index)}
                            className="text-[#FF6B4A]"
                          >
                            <MdCloseComponent />
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        value={bucket.name || ''}
                        onChange={(e) =>
                          handleBucketChange(index, 'name', e.target.value)
                        }
                        className="border rounded p-2 w-full text-sm mb-2"
                        placeholder="Branch name"
                      />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={bucket.weight || 0}
                        onChange={(e) =>
                          handleBucketChange(
                            index,
                            'weight',
                            parseInt(e.target.value) || 0
                          )
                        }
                        className="border rounded p-2 w-full text-sm"
                      />
                    </div>
                  ))}
                  <button
                    onClick={handleAddBucket}
                    className="w-full border border-dashed border-[#00A4BD] rounded p-2 text-[#00A4BD] text-sm"
                  >
                    + Add Branch
                  </button>
                </div>
              </>
            )}

            {/* ===== MULTI SPLIT ===== */}
            {(node.data.type === 'multi' || node.data.isMultiSplit) &&
              node.data.multiSplitRules && (
                <>
                  <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                    MULTI SPLIT RULES
                  </div>
                  <div className="space-y-3">
                    {node.data.multiSplitRules.map((rule, index) => (
                      <div
                        key={rule.id}
                        className="bg-[#F5F8FA] border border-[#DAE4F2] rounded p-3"
                      >
                        <div className="flex justify-between items-center mb-2">
                          <div className="text-[12px] font-medium text-[#3F5265]">
                            {rule.label || `Rule ${index + 1}`}
                          </div>
                        </div>
                        <div className="text-[13px] text-[#3F5265] bg-white p-2 rounded border border-gray-100">
                          {rule.condition
                            ? formatConditionDisplay(rule.condition)
                            : 'Default'}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

            {/* ===== BRANCH (True/False) ===== */}
            {(node.data.type === 'branch' || node.data.isBranch) && (
              <>
                <div className="text-[12px] mb-2 mt-4 text-[#9FA9B2]">
                  BRANCH CONDITIONS
                </div>
                <div className="text-xs text-gray-500 mb-3">
                  Define a condition to split your flow. Contacts that match
                  will go to the "True" path, others to "False".
                </div>
                <div className="space-y-2 mb-4">
                  {branchConditions.map((cond, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 border border-gray-200 rounded p-3"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">
                          Condition {index + 1}
                        </span>
                        <button
                          onClick={() => removeBranchCondition(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <MdCloseComponent size={16} />
                        </button>
                      </div>
                      <div className="text-xs text-blue-600 mt-1 font-medium">
                        {formatConditionDisplay(cond)}
                      </div>
                    </div>
                  ))}
                </div>
                {!showConditionSelect ? (
                  <button
                    onClick={() => setShowConditionSelect(true)}
                    className="w-full border border-dashed border-[#00A4BD] rounded p-2 text-[#00A4BD] text-sm hover:bg-blue-50"
                  >
                    + Add Condition
                  </button>
                ) : (
                  <div className="border border-blue-200 rounded p-3 bg-blue-50">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Select Condition
                    </label>
                    <select
                      value={selectedValue}
                      onChange={(e) => setSelectedValue(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#00A4BD]"
                    >
                      <option value="">-- Choose a condition --</option>
                      {conditionOptions.map((group, idx) => (
                        <optgroup key={idx} label={group.category}>
                          {group.options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={addBranchCondition}
                        disabled={!selectedValue}
                        className={`px-4 py-1.5 rounded text-sm ${selectedValue
                          ? 'bg-[#00A4BD] text-white hover:bg-[#008BA0]'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowConditionSelect(false);
                          setSelectedValue('');
                        }}
                        className="px-4 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
                      <span>True path</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span>
                      <span>False path</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Preview Modal (separate component) */}
        <EmailPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          previewHtml={previewHtml}
          previewLoading={previewLoading}
        />
      </div>
    );
  }
);

export default DetailPanel;